import { ExternalLink, Eye, Pencil, X } from 'lucide-react'
import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react'
import { useBlocker, useNavigate } from 'react-router-dom'

import { RelativeTime, UserAvatar } from '@/shared/components/common'
import { ErrorState, SkeletonRows, errorMessage } from '@/shared/components/data'
import { Button, Markdown, Portal, Segmented, Switch } from '@/shared/components/ui'
import type { SegmentedOption } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'

import { isConflictError, useDocument, useUpdateDocument } from './api'
import { ConflictDialog } from './components/ConflictDialog'
const MarkdownEditor = lazy(() => import('./components/MarkdownEditor'))
import { docTypeLabel, type DocumentDetail } from './types'

export type DocMode = 'view' | 'edit' | 'split'

/** El conmutador se arma con el idioma puesto, no en el módulo. */
function useModes(): readonly SegmentedOption<DocMode>[] {
  const t = useT()
  return useMemo(
    () => [
      { value: 'view', label: t('docs.mode.view') },
      { value: 'edit', label: t('docs.mode.edit') },
      { value: 'split', label: t('docs.mode.split') },
    ],
    [t],
  )
}

export interface MarkdownDocProps {
  documentId: string
  /** `can(me, 'document:write')`. En false el documento es de solo lectura. */
  canWrite: boolean
  /** A dónde vuelve `Esc` y el botón de cerrar. */
  backTo: string
}

/**
 * Un documento: viewer + editor.
 *
 * - El viewer pasa por `react-markdown` + `remark-gfm` + **`rehype-sanitize`**.
 *   El sanitizado no es opcional: esto es texto escrito por personas y que en
 *   el portal termina leyendo un cliente.
 * - `Cmd/Ctrl+S` guarda. `Esc` sale, y si hay cambios sin guardar el
 *   `useBlocker` de React Router lo frena y pregunta.
 * - Un 409 abre el diálogo con **las dos versiones**.
 * - Los `kind: 'link'` no tienen editor: se abren afuera.
 */
export function MarkdownDoc({ documentId, canWrite, backTo }: MarkdownDocProps) {
  const t = useT()
  const query = useDocument(documentId)
  const document = query.data

  if (query.isPending) return <SkeletonRows rows={8} withSpine={false} />
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />
  if (!document) return <ErrorState title={t('docs.notFound')} />

  return <DocumentBody key={document.id} document={document} canWrite={canWrite} backTo={backTo} />
}

interface DocumentBodyProps {
  document: DocumentDetail
  canWrite: boolean
  backTo: string
}

function DocumentBody({ document, canWrite, backTo }: DocumentBodyProps) {
  const t = useT()
  const modes = useModes()
  const navigate = useNavigate()
  const update = useUpdateDocument(document.id)
  const { mutateAsync } = update

  const saved = document.content_md ?? ''
  const [draft, setDraft] = useState(saved)
  const [mode, setMode] = useState<DocMode>('view')
  const [conflict, setConflict] = useState<DocumentDetail | null>(null)
  const [justSaved, setJustSaved] = useState(false)

  const dirty = canWrite && mode !== 'view' && draft !== saved

  const save = useCallback(
    async (expectedVersion: number) => {
      try {
        const result = await mutateAsync({ content_md: draft, expected_version: expectedVersion })
        setDraft(result.content_md ?? '')
        setConflict(null)
        setJustSaved(true)
      } catch (error) {
        if (isConflictError(error)) setConflict(error.current)
      }
    },
    [draft, mutateAsync],
  )

  // Bloquea la navegación mientras haya cambios sin guardar. El `Esc` de abajo
  // navega igual: es este blocker el que decide si la salida se concreta.
  const blocker = useBlocker(
    useCallback(
      ({
        currentLocation,
        nextLocation,
      }: {
        currentLocation: { pathname: string }
        nextLocation: { pathname: string }
      }) => dirty && currentLocation.pathname !== nextLocation.pathname,
      [dirty],
    ),
  )

  useEffect(() => {
    if (!dirty) return
    const warn = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  useEffect(() => {
    if (!justSaved) return
    const timer = setTimeout(() => setJustSaved(false), 2000)
    return () => clearTimeout(timer)
  }, [justSaved])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
        event.preventDefault()
        if (canWrite && mode !== 'view' && document.kind === 'markdown') {
          void save(document.current_version)
        }
        return
      }
      if (event.key === 'Escape' && !conflict) {
        event.preventDefault()
        void navigate(backTo)
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [backTo, canWrite, conflict, document.current_version, document.kind, mode, navigate, save])

  const editing = mode === 'edit' || mode === 'split'

  return (
    <div className="flex flex-col gap-3">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-1">
          <h2 className="font-display text-md text-text-1">{document.title}</h2>
          <p className="flex flex-wrap items-center gap-2 text-2xs text-text-3">
            <span>{docTypeLabel(t, document.doc_type)}</span>
            <span aria-hidden="true">·</span>
            <span className="num">{t('docs.version', { version: document.current_version })}</span>
            <span aria-hidden="true">·</span>
            <UserAvatar user={document.updated_by} withName />
            <RelativeTime value={document.updated_at} />
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span aria-live="polite" className="text-2xs text-text-3">
            {update.isPending
              ? t('common.state.saving')
              : justSaved
                ? t('common.state.saved')
                : dirty
                  ? t('docs.state.unsaved')
                  : ''}
          </span>

          {document.kind === 'markdown' && canWrite ? (
            <>
              <Segmented
                value={mode}
                options={modes}
                onChange={setMode}
                label={t('docs.mode.label')}
              />
              <Button
                variant="primary"
                disabled={!dirty || update.isPending}
                onClick={() => void save(document.current_version)}
              >
                {t('common.action.save')}
              </Button>
            </>
          ) : null}

          <Button onClick={() => void navigate(backTo)} aria-label={t('docs.close')}>
            <X size={13} aria-hidden="true" />
            {t('common.action.close')}
          </Button>
        </div>
      </header>

      <VisibilityBanner document={document} canWrite={canWrite} />

      {document.kind === 'link' ? (
        <LinkCard document={document} />
      ) : (
        <div className={cn('grid gap-3', mode === 'split' && 'lg:grid-cols-2')}>
          {editing ? (
            <Suspense
              fallback={
                <div className="hairline min-h-[320px] rounded-[var(--radius-card)] bg-surface-1" />
              }
            >
              <MarkdownEditor
                value={draft}
                onChange={setDraft}
                label={t('docs.editor.label', { titulo: document.title })}
              />
            </Suspense>
          ) : null}

          {mode !== 'edit' ? (
            <article className="hairline rounded-[var(--radius-card)] bg-surface-1 px-4 py-3">
              <Markdown empty={t('docs.emptyContent')}>{editing ? draft : saved}</Markdown>
            </article>
          ) : null}
        </div>
      )}

      {document.kind === 'markdown' && !canWrite ? (
        <p className="text-2xs text-text-3">{t('docs.readOnly')}</p>
      ) : null}

      {document.kind === 'markdown' && canWrite && mode === 'view' ? (
        <p className="flex items-center gap-1.5 text-2xs text-text-3">
          <Pencil size={11} aria-hidden="true" />
          {t('docs.editHint', { modo: t('docs.mode.edit'), guardar: '⌘S', salir: 'Esc' })}
        </p>
      ) : null}

      {/*
        Un 409 lo cuenta el diálogo, nunca esta línea: `errorMessage` devolvería
        el `message` del `DocumentConflictError`, que es texto de desarrollo y
        no pasa por el catálogo. Y después de "Descartar" el error sigue en la
        mutación aunque el conflicto ya esté resuelto.
      */}
      {update.isError && !conflict && !isConflictError(update.error) ? (
        <p role="alert" className="text-2xs" style={{ color: 'hsl(var(--overdue))' }}>
          {errorMessage(update.error, t('docs.error.save'))}
        </p>
      ) : null}

      {conflict ? (
        <ConflictDialog
          mine={draft}
          theirs={conflict}
          saving={update.isPending}
          onKeepMine={() => void save(conflict.current_version)}
          onDiscardMine={() => {
            setDraft(conflict.content_md ?? '')
            setConflict(null)
          }}
        />
      ) : null}

      {blocker.state === 'blocked' ? (
        <UnsavedDialog onLeave={() => blocker.proceed?.()} onStay={() => blocker.reset?.()} />
      ) : null}
    </div>
  )
}

function VisibilityBanner({ document, canWrite }: { document: DocumentDetail; canWrite: boolean }) {
  const t = useT()
  const update = useUpdateDocument(document.id)
  const visible = document.visibility === 'client'

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-2 rounded-[var(--radius-card)] px-3 py-2',
        'border bg-surface-1',
      )}
      style={{
        borderColor: visible ? 'hsl(var(--signal) / .4)' : 'hsl(var(--hairline))',
      }}
    >
      <p className="flex items-center gap-2 text-xs text-text-2">
        {visible ? (
          <Eye size={13} aria-hidden="true" style={{ color: 'hsl(var(--signal))' }} />
        ) : (
          <Eye size={13} aria-hidden="true" className="text-text-3" />
        )}
        {visible ? t('docs.visibility.clientBanner') : t('docs.visibility.internalBanner')}
      </p>

      <Switch
        checked={visible}
        disabled={!canWrite || update.isPending}
        label={t('docs.visibility.toggle')}
        onChange={(next) => update.mutate({ visibility: next ? 'client' : 'internal' })}
      />
    </div>
  )
}

function LinkCard({ document }: { document: DocumentDetail }) {
  const t = useT()

  return (
    <div className="hairline flex flex-wrap items-center gap-3 rounded-[var(--radius-card)] bg-surface-1 px-3 py-3">
      <ExternalLink size={16} aria-hidden="true" className="text-text-3" />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-xs text-text-2">{t('docs.link.description')}</p>
        <span className="num truncate text-2xs text-text-3">{document.external_url}</span>
      </div>
      {document.external_url ? (
        <a
          href={document.external_url}
          target="_blank"
          rel="noreferrer"
          className="hairline inline-flex h-7 items-center gap-1.5 rounded-[var(--radius-control)] px-3 text-xs text-text-1 hover:bg-surface-2"
        >
          {t('common.action.open')}
          <ExternalLink size={12} aria-hidden="true" />
        </a>
      ) : null}
    </div>
  )
}

function UnsavedDialog({ onLeave, onStay }: { onLeave: () => void; onStay: () => void }) {
  const t = useT()

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-bg/70 p-4">
        <div
          role="alertdialog"
          aria-modal="true"
          aria-label={t('docs.unsaved.label')}
          className="hairline flex w-full max-w-[420px] flex-col gap-3 rounded-[var(--radius-card)] bg-surface-1 p-4"
        >
          <h2 className="font-display text-base text-text-1">{t('docs.unsaved.title')}</h2>
          <p className="text-xs text-text-2">{t('docs.unsaved.description')}</p>
          <div className="flex justify-end gap-2">
            <Button onClick={onStay}>{t('docs.unsaved.stay')}</Button>
            <Button variant="danger" onClick={onLeave}>
              {t('docs.unsaved.leave')}
            </Button>
          </div>
        </div>
      </div>
    </Portal>
  )
}
